import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../utils/supabase';
import { getPlayerNameColor } from '../../utils/playerUtils';

// ELO bands (min inclusive, max inclusive) to sample from so we show a spread across the range
const ELO_BANDS = [
  [1800, 2000],
  [1600, 1799],
  [1400, 1599],
  [1200, 1399],
  [1000, 1199],
  [800, 999]
];

/**
 * Sample up to maxPerBand players from each ELO band, then sort by ELO descending.
 * @param {Array} list - Full list of items with eloKey
 * @param {string} eloKey - 'mixed_elo_rating' or 'levels_elo_rating'
 * @param {number} maxPerBand - Max players to take per band
 * @returns {Array} Sampled list sorted by ELO desc
 */
function sampleByBands(list, eloKey, maxPerBand = 2) {
  const result = [];
  for (const [low, high] of ELO_BANDS) {
    const inBand = list.filter((item) => {
      const elo = item[eloKey] ?? 1200;
      return elo >= low && elo <= high;
    });
    const taken = inBand.slice(0, maxPerBand);
    result.push(...taken);
  }
  return result.sort((a, b) => (b[eloKey] ?? 1200) - (a[eloKey] ?? 1200));
}

/**
 * Reusable compact club leaderboard for Mixed and Levels (Men's/Women's) ELO.
 * Used in Add Guest modal and can be used on club pages.
 *
 * @param {string} clubId - Club ID
 * @param {string} [seasonId] - Season ID (optional; resolves current season if not provided)
 * @param {number} [limit=8] - Max players per column when useBands is false
 * @param {boolean} [useBands=true] - If true, sample from each ELO band for full range; if false, show top N
 * @param {number} [maxPerBand=2] - When useBands, max players per band
 * @param {'male'|'female'|null} [levelsGender] - Which levels column to show (Men's or Women's)
 * @param {boolean} [compact=true] - Compact styling for modals
 */
const ClubLeaderboardPreview = ({
  clubId,
  seasonId: seasonIdProp,
  limit = 8,
  useBands = true,
  maxPerBand = 2,
  levelsGender = null,
  compact = true
}) => {
  const [seasonId, setSeasonId] = useState(seasonIdProp);
  const [mixedRaw, setMixedRaw] = useState([]);
  const [levelsMaleRaw, setLevelsMaleRaw] = useState([]);
  const [levelsFemaleRaw, setLevelsFemaleRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clubId) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let seasonIdToUse = seasonIdProp;
        if (!seasonIdToUse) {
          const { data: currentSeason } = await supabase
            .from('seasons')
            .select('id')
            .eq('club_id', clubId)
            .eq('season_status', 'current')
            .maybeSingle();
          seasonIdToUse = currentSeason?.id;
        }
        if (cancelled || !seasonIdToUse) {
          setLoading(false);
          return;
        }
        setSeasonId(seasonIdToUse);

        // Fetch enough to cover full range (no small limit so we can band)
        const [
          { data: levelsData, error: levelsErr },
          { data: mixedData, error: mixedErr }
        ] = await Promise.all([
          supabase
            .from('player_club_elo_ratings')
            .select(`
              levels_elo_rating,
              players:player_id (id, first_name, last_name, gender, is_guest, is_active)
            `)
            .eq('club_id', clubId)
            .eq('season_id', seasonIdToUse)
            .not('levels_elo_rating', 'is', null)
            .order('levels_elo_rating', { ascending: false }),
          supabase
            .from('player_club_elo_ratings')
            .select(`
              mixed_elo_rating,
              players:player_id (id, first_name, last_name, gender, is_guest, is_active)
            `)
            .eq('club_id', clubId)
            .eq('season_id', seasonIdToUse)
            .not('mixed_elo_rating', 'is', null)
            .order('mixed_elo_rating', { ascending: false })
        ]);

        if (levelsErr || mixedErr) {
          throw levelsErr || mixedErr;
        }

        const isNotArchivedGuest = (player) => {
          if (!player) return false;
          return !(player.is_guest === true && player.is_active === false);
        };

        const mixedFiltered = (mixedData || []).filter((item) => isNotArchivedGuest(item.players));
        const levelsMaleFiltered = (levelsData || []).filter(
          (item) => isNotArchivedGuest(item.players) && item.players?.gender === 'male'
        );
        const levelsFemaleFiltered = (levelsData || []).filter(
          (item) => isNotArchivedGuest(item.players) && item.players?.gender === 'female'
        );

        if (!cancelled) {
          setMixedRaw(mixedFiltered);
          setLevelsMaleRaw(levelsMaleFiltered);
          setLevelsFemaleRaw(levelsFemaleFiltered);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[ClubLeaderboardPreview]', err);
          setError(err?.message || 'Failed to load leaderboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [clubId, seasonIdProp]);

  const mixed = useMemo(() => {
    if (useBands) return sampleByBands(mixedRaw, 'mixed_elo_rating', maxPerBand);
    return mixedRaw.slice(0, limit);
  }, [mixedRaw, useBands, maxPerBand, limit]);

  const levelsMale = useMemo(() => {
    if (useBands) return sampleByBands(levelsMaleRaw, 'levels_elo_rating', maxPerBand);
    return levelsMaleRaw.slice(0, limit);
  }, [levelsMaleRaw, useBands, maxPerBand, limit]);

  const levelsFemale = useMemo(() => {
    if (useBands) return sampleByBands(levelsFemaleRaw, 'levels_elo_rating', maxPerBand);
    return levelsFemaleRaw.slice(0, limit);
  }, [levelsFemaleRaw, useBands, maxPerBand, limit]);

  const levelsList = levelsGender === 'female' ? levelsFemale : levelsMale;
  const levelsLabel = levelsGender === 'female' ? "Women's Doubles" : "Men's Doubles";

  if (!clubId) return null;

  const rowClass = compact
    ? 'flex justify-between gap-2 py-0.5 text-xs'
    : 'flex items-center justify-between rounded-lg border border-border-light bg-surface-subtle p-2 text-sm';
  const nameClass = compact
    ? 'min-w-0 truncate text-text-secondary'
    : 'font-medium text-text-primary';
  const eloClass = compact
    ? 'shrink-0 tabular-nums font-semibold text-primary'
    : 'text-lg font-bold text-primary';

  const renderColumn = (title, items, eloKey, { useGenderColors = false } = {}) => (
    <div className={compact ? '' : 'space-y-2'}>
      <p
        className={`mb-1 font-medium text-text-primary ${compact ? 'text-xs' : 'text-sm'}`}
      >
        {title}
      </p>
      {items.length === 0 ? (
        <p className="text-xs text-text-muted">No data</p>
      ) : (
        <ul className="space-y-0.5">
          {items.map((item, index) => {
            const p = item.players || item.player;
            const name = p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown' : 'Unknown';
            const elo = item[eloKey] ?? 1200;
            const playerNameClass = useGenderColors
              ? `min-w-0 truncate ${getPlayerNameColor(p?.gender)}`
              : nameClass;
            return (
              <li key={p?.id || index} className={rowClass}>
                <span className={playerNameClass}>{name}</span>
                <span className={eloClass}>{elo}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  if (loading) {
    return <p className="text-xs text-text-muted">Loading leaderboard...</p>;
  }
  if (error) {
    return <p className="text-xs text-danger-dark">{error}</p>;
  }

  return (
    <div className={`grid grid-cols-2 gap-3 ${compact ? 'text-xs' : ''}`}>
      {renderColumn('Mixed Doubles', mixed, 'mixed_elo_rating', { useGenderColors: true })}
      {renderColumn(levelsLabel, levelsList, 'levels_elo_rating')}
    </div>
  );
};

export default ClubLeaderboardPreview;
